import React, { useMemo } from "react";
import { Alert, Button, Group, Stack, Text } from "@mantine/core";
import type { IBlobMedia } from "@multiconsult-group/multiplacer-core";

import { useAppDispatch } from "../../app/hooks";
import { uploadDeployNoteMediaThunk } from "./uploadDeployNoteMedia";
import { BlobMediaPreview } from "../BlobMedia";
import { useDeleteMultiplacerReleaseNotesDeployNotesByDeployNoteIdChangeItemsAndChangeItemIdMediaMutation } from "../../../openapi";

// ✅ OpenAPI/RTK mutation for delete

type Props = {
  deployNoteId?: string;
  changeItemId?: string;
  disabled?: boolean;
  existingMedia?: IBlobMedia[] | undefined;
  onUploaded?: (media: IBlobMedia) => void;

  // Optional: parent kan oppdatere state/refresh etter sletting
  onDeleted?: (blobPath: string) => void;
};

export const DeployChangeItemMediaUpload: React.FC<Props> = ({
  deployNoteId,
  changeItemId,
  disabled,
  existingMedia,
  onUploaded,
  onDeleted,
}) => {
  const dispatch = useAppDispatch();

  const [deleteMedia, { isLoading: isDeleting }] =
    useDeleteMultiplacerReleaseNotesDeployNotesByDeployNoteIdChangeItemsAndChangeItemIdMediaMutation();

  const isReady = useMemo(() => {
    return !disabled && !!deployNoteId && !!changeItemId;
  }, [disabled, deployNoteId, changeItemId]);

  const handlePickFile: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.currentTarget.files?.[0];
    e.currentTarget.value = "";
    if (!file) return;

    if (!deployNoteId || !changeItemId) return;

    const res = await dispatch(
      uploadDeployNoteMediaThunk({ deployNoteId, changeItemId, file })
    ).unwrap();
    onUploaded?.(res.media);
  };

  const handleDelete = async (blobPath: string) => {
    if (!deployNoteId || !changeItemId) return;

    await deleteMedia({
      deployNoteId,
      changeItemId,
      blobPath,
    } as any).unwrap();

    onDeleted?.(blobPath);
  };

  return (
    <Stack gap="xs">
      {!isReady && (
        <Alert variant="light">
          Lagre deploy-notatet først, så kan du laste opp bilde/video på hvert change item.
        </Alert>
      )}

      <Group justify="space-between" align="center">
        <Text fw={600}>Media</Text>

        <Button component="label" variant="light" disabled={!isReady}>
          Last opp bilde/video
          <input type="file" hidden accept="image/*,video/*" onChange={handlePickFile} />
        </Button>
      </Group>

      {(existingMedia?.length ?? 0) > 0 && (
        <Stack gap="xs">
          {(existingMedia ?? []).map((m, idx) => (
            <Stack key={idx} gap={6}>
              <Group justify="space-between" align="center">
                <Text size="sm" c="dimmed">
                  • {m.kind}: {m.caption ?? m.blobPath}
                </Text>

                <Button
                  size="xs"
                  variant="subtle"
                  color="red"
                  disabled={!isReady}
                  loading={isDeleting}
                  onClick={() => handleDelete(m.blobPath)}
                >
                  Slett
                </Button>
              </Group>

              <BlobMediaPreview media={m} target="release-notes" />
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
};
